import type { ComponentProps } from 'svelte';
import { modal } from '$stores/modalStore';

import ModalUserSelect from './ModalUserSelect.svelte';
import ModalDivisionSelect from './ModalDivisionSelect.svelte';
import type { Modal, SizeType } from '@allibee/components/basic';
import { alert, toastDanger } from '$lib/utils';
import { page } from '$app/stores';
import { get } from 'svelte/store';
import { _ } from 'svelte-i18n';

type originAlertProps = Extract<Parameters<typeof alert>[0], object>;
type AlertProps = originAlertProps & {
    color?: originAlertProps['color'] | 'primary';
};
type ModalUserSelectProps = {
    onConfirm?: (v?: any) => any;
    confirmText?: string;
    onCancel?: (v?: any) => void;
    multiple?: boolean;
    defaultItems?: MtmUserDto[] | undefined;
    defaultDivisionName?: string | undefined;
    allowedUsers?: Array<{ value: number; label: string; data: MtmUserDto }> | undefined;
};
type ModalDivisionSelectProps = {
    onConfirm?: (v?: any) => any;
    confirmText?: string;
    onCancel?: (v?: any) => void;
    multiple?: boolean;
    color?: 'primary' | 'hanwhas';
    defaultItems?: AlexGroupDto[] | undefined;
    includeSubDivision?: boolean | undefined;
};

/**
 * 사용자 선택 모달을 엽니다.
 *
 * @param props - ModalUserSelect 컴포넌트에 전달할 속성입니다. `multiple`을 포함하여 다중 선택을 활성화할 수 있습니다.
 * @param modalProps - 모달에 대한 추가 속성입니다.
 * @returns 모달 결과를 포함하는 Promise를 반환합니다.
 *
 * @example
 * ```typescript
 * // 다중 선택 모달을 여는 예시
 * const res = await modalUserSelect({ multiple: true });
 *
 * // 단일 선택 모달을 여는 예시
 * const res = await modalUserSelect();
 * ```
 */
export function modalUserSelect(props?: ModalUserSelectProps | string, modalProps?: ComponentProps<Modal>) {
    let size = 'lg' as SizeType;
    let resolvedProps: ModalUserSelectProps = {};

    if (typeof props === 'string') {
        resolvedProps = {};
    } else if (props && 'multiple' in props) {
        size = props.multiple ? 'xl' : 'lg';
        resolvedProps = props;
    } else if (props) {
        resolvedProps = props;
    }

    return modal.open(
        ModalUserSelect,
        { defaultItems: undefined, allowedUsers: undefined, ...resolvedProps },
        { ...modalProps, size, permanent: true, outsideclose: false, autoclose: false, maxHeight: '46.875rem', ...modalProps },
        'custom',
    );
}

/**
 * 부서 선택 모달을 엽니다.
 *
 * @param props - ModalDivisionSelect 컴포넌트에 전달할 속성입니다. `multiple`을 포함하여 다중 선택을 활성화할 수 있습니다.
 * @param modalProps - 모달에 대한 추가 속성입니다.
 * @returns 모달 결과를 포함하는 Promise를 반환합니다.
 *
 * @example
 * ```typescript
 * // 다중 선택 모달을 여는 예시
 * const res = await modalDivisionSelect({ multiple: true });
 *
 * // 단일 선택 모달을 여는 예시
 * const res = await modalDivisionSelect();
 * ```
 */
export function modalDivisionSelect(props?: ModalDivisionSelectProps | string, modalProps?: ComponentProps<Modal>) {
    let size = 'md' as SizeType;
    let resolvedProps: ModalDivisionSelectProps = {};

    if (typeof props === 'string') {
        resolvedProps = {};
    } else if (props && 'multiple' in props) {
        size = props.multiple ? 'lg' : 'md';
        resolvedProps = props;
    } else if (props) {
        resolvedProps = props;
    }

    return modal.open(
        ModalDivisionSelect,
        { defaultItems: undefined, ...resolvedProps },
        { ...modalProps, size, permanent: true, outsideclose: false, autoclose: false, maxHeight: '46.875rem' },
        'custom',
    );
}

/**
 * 스토리지 용량 부족 알림 모달 및 결제/청구 페이지를 새탭으로 엽니다. 권한이 없는 경우 토스트 메시지를 표시합니다.
 *
 * @param alertProps - 알림 모달에 대한 추가 속성입니다.
 * @returns 모달 결과를 포함하는 Promise를 반환합니다.
 */
export async function alertStorageShortage(alertProps: Partial<AlertProps> = {}) {
    const { auth, master, workspaceName } = get(page).data;
    const { transformUrl } = master;
    const { hasPermission } = auth;
    if (hasPermission('workspace.order.publish') && hasPermission('workspace.order.pay')) {
        return alert({
            color: 'primary',
            // title: '스토리지 용량 부족',
            title: get(_)('apps.contract.lib.service.modalContent.alertStorageShortage.title'),
            // msg: '스토리지 용량 부족으로 해당 기능을 사용할 수 없습니다. 기존 데이터을 삭제하거나 제품 구매 또는 플랜 업그레이드 후 다시 시도해 주세요.',
            msg: get(_)('apps.contract.lib.service.modalContent.alertStorageShortage.msg'),
            // confirmText: '제품 구매 / 플랜 업그레이드',
            confirmText: get(_)('apps.contract.lib.service.modalContent.alertStorageShortage.confirmText'),
            onConfirm: () => {
                // 결제 / 청구 페이지로 새탭 열기
                window.open(transformUrl(`/${workspaceName}/admin/payment/`), '_blank');
            },
            ...alertProps,
        } as AlertProps);
    } else {
        // 권한이 없는 경우 관리자에게 연락하라는 토스트
        toastDanger(get(_)('apps.contract.lib.service.modalContent.alertStorageShortage.toast'));
        return true;
    }
}
